import { useMutation, useQuery } from '@apollo/client';
import {
  orderDocument,
  ordersDocument,
  personalPickupDeliveredOrdersDocument,
  personalPickupOrdersDocument,
} from './features/order/graphql/queries';
import { DataTable } from 'mantine-datatable';
import {
  ActionIcon,
  Anchor,
  Avatar,
  Badge,
  Button,
  Divider,
  Group,
  NumberInput,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { OrderLineStatus, OrderLineType, OrderStatus } from './__generated__/graphql';
import { useEffect, useState } from 'react';
import { IconCheck, IconRefresh, IconSend } from '@tabler/icons-react';
import { getFragmentData } from './__generated__';
import { orderLineDetailsFragment } from './features/order/graphql/fragments';
import { useDebouncedValue, useDocumentVisibility, useInputState } from '@mantine/hooks';
import {
  sendMessageDocument,
  sendMessagesDocument,
  updateOrderDocument,
} from './features/order/graphql/mutations';
import { showNotification } from '@mantine/notifications';
import { modals } from '@mantine/modals';

const lineStatusColors = {
  [OrderLineStatus.Created]: 'gray',
  [OrderLineStatus.Packed]: 'blue',
  [OrderLineStatus.Confirmed]: 'green',
  [OrderLineStatus.Reviewing]: 'orange',
  [OrderLineStatus.Returned]: 'red',
};

const lineStatusTranslations = {
  [OrderLineStatus.Created]: 'Objednané',
  [OrderLineStatus.Packed]: 'Zabalené',
  [OrderLineStatus.Confirmed]: 'Potvrdené',
  [OrderLineStatus.Reviewing]: 'V riešení',
  [OrderLineStatus.Returned]: 'Vrátené',
};

const PersonalOrderTable = ({ id }: { id: string }) => {
  const { data, loading } = useQuery(orderDocument, {
    variables: {
      where: { id },
    },
  });

  const lines = getFragmentData(orderLineDetailsFragment, data?.order?.lines);

  return (
    <DataTable
      withTableBorder
      striped
      highlightOnHover
      records={
        lines?.filter(
          ({ type }) => type === OrderLineType.Product || type === OrderLineType.Custom
        ) ?? []
      }
      borderRadius="sm"
      fetching={loading}
      columns={[
        {
          accessor: 'code',
          title: 'Kód',
          render: ({ code, type }) =>
            OrderLineType.Product || type === OrderLineType.Custom ? code : '',
          footer: `${lines?.filter(({ type }) => type === OrderLineType.Product || type === OrderLineType.Custom).length} ks`,
        },
        { accessor: 'name', title: 'Názov' },
        {
          accessor: 'description',
          title: 'Popis',
        },
        {
          accessor: 'price',
          title: 'Cena',
          render: ({ price }) => `${price} €`,
          footer: `${lines?.reduce((sum, line) => sum + line.price, 0)} €`,
        },
        {
          accessor: 'status',
          title: 'Stav',
          render: ({ status }) => (
            <Badge color={lineStatusColors[status]}>{lineStatusTranslations[status]}</Badge>
          ),
        },
      ]}
    />
  );
};

const getDayDiff = (date: Date | string) =>
  dayjs().startOf('day').diff(dayjs(date).startOf('day'), 'day');

const MessageEditor = ({
  initial,
  valueRef,
}: {
  initial: string;
  valueRef: { current: string };
}) => {
  const [message, setMessage] = useState(initial);
  return (
    <Textarea
      autosize
      minRows={3}
      maxRows={10}
      value={message}
      onChange={(e) => {
        const v = e.currentTarget.value;
        setMessage(v);
        valueRef.current = v;
      }}
    />
  );
};

export const PersonalPage = () => {
  const [packageNumber, setPackageNumber] = useState<string | number>('');
  const [customerName, setCustomerName] = useInputState<string>('');

  const visibility = useDocumentVisibility();

  const hasPackage = packageNumber !== '';
  const trimmedName = customerName.trim();
  const hasName = trimmedName.length > 0;

  const [debouncedPackage] = useDebouncedValue(packageNumber, 500);
  const [debouncedName] = useDebouncedValue(trimmedName, 500);

  const debouncedHasPackage = debouncedPackage !== '';
  const debouncedHasName = debouncedName.length > 0;
  const search =
    debouncedHasPackage || debouncedHasName
      ? {
          package: debouncedHasPackage ? Number(debouncedPackage) : null,
          name: debouncedHasName ? debouncedName : null,
        }
      : null;

  const {
    data,
    loading,
    refetch: refetchOrders,
  } = useQuery(personalPickupOrdersDocument, {
    variables: { search },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const personalOrders = data?.personalPickupOrders.orders ?? [];

  const {
    data: delivered,
    loading: deliveredLoading,
    refetch: refetchDelivered,
  } = useQuery(personalPickupDeliveredOrdersDocument, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const deliveredOrders = delivered?.personalPickupDeliveredOrders.orders ?? [];

  const [updateOrder, updateOrderMutation] = useMutation(updateOrderDocument, {
    refetchQueries: [
      ordersDocument,
      personalPickupOrdersDocument,
      personalPickupDeliveredOrdersDocument,
    ],
    awaitRefetchQueries: true,
  });

  const [sendMessage, sendMessageMutation] = useMutation(sendMessageDocument);
  const [sendMessages, sendMessagesMutation] = useMutation(sendMessagesDocument);

  useEffect(() => {
    if (visibility !== 'visible') return;

    refetchOrders();
    refetchDelivered();
  }, [refetchDelivered, refetchOrders, visibility]);

  return (
    <Stack>
      <Group justify="space-between">
        <NumberInput
          label="Číslo balíka"
          placeholder="Zadaj číslo balíka..."
          value={packageNumber}
          onChange={setPackageNumber}
          min={1}
          allowNegative={false}
          allowDecimal={false}
          hideControls
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.blur();
            }
          }}
        />
        <TextInput
          label="Meno zákazníka (s diakritikou)"
          placeholder="Zadaj meno zákazníka s diakritikou..."
          value={customerName}
          onChange={setCustomerName}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.blur();
            }
          }}
        />

        <Stack gap={4}>
          <Text c="orange">Počet objednávok: {data?.personalPickupOrders.count ?? 0}</Text>
          <Text c="orange">Na vyzdvihnutie: {data?.personalPickupOrders.total ?? 0} €</Text>
          <Text c="teal">
            Dnes prevzaté: {delivered?.personalPickupDeliveredOrders.count ?? 0} (
            {delivered?.personalPickupDeliveredOrders.total ?? 0} €)
          </Text>
          <Button
            size="sm"
            loading={sendMessagesMutation.loading}
            leftSection={<IconSend size={16} />}
            onClick={() => {
              const defaultMessage = `Pripomíname, že Vaša objednávka stále čaká na predajni Sama Chalupku 2, dvere č. 3. Príďte si ju prosím vyzdvihnúť, inak ju budeme musieť zrušiť.`;
              const messageRef = { current: defaultMessage };

              modals.openConfirmModal({
                title: 'Odoslať správy',
                children: (
                  <Stack gap="xs">
                    <Text fw={500}>
                      Chcete odoslať správu všetkým {data?.personalPickupOrders.count} zákazníkom,
                      ktorí si ešte nevyzdvihli svoje objednávky?
                    </Text>
                    <Divider />
                    <Text fz="xs" c="dimmed">
                      Správa, ktorá bude odoslaná (môžete upraviť):
                    </Text>
                    <MessageEditor initial={defaultMessage} valueRef={messageRef} />
                  </Stack>
                ),
                labels: { confirm: 'Odoslať', cancel: 'Nie' },
                onConfirm: () =>
                  sendMessages({
                    variables: {
                      orderIds: personalOrders.map(({ id }) => id),
                      message: messageRef.current,
                    },
                  }),
              });
            }}
          >
            Poslať správy
          </Button>
        </Stack>
      </Group>
      {(hasPackage || hasName) && (
        <Group>
          {hasPackage && (
            <Badge
              color="blue"
              size="lg"
              radius="sm"
              onClick={() => setPackageNumber('')}
              style={{ cursor: 'pointer' }}
            >
              Balík: {packageNumber}
            </Badge>
          )}
          {hasName && (
            <Badge
              color="green"
              size="lg"
              radius="sm"
              onClick={() => setCustomerName('')}
              style={{ cursor: 'pointer' }}
            >
              Zákazník: {customerName}
            </Badge>
          )}
        </Group>
      )}
      <Tabs defaultValue="orders" variant="pills" keepMounted={false}>
        <Tabs.List grow>
          <Tabs.Tab value="orders">Neprevzaté</Tabs.Tab>
          <Tabs.Tab value="delivered">Prevzaté</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="orders">
          <DataTable
            withTableBorder
            striped
            highlightOnHover
            records={personalOrders}
            borderRadius="sm"
            minHeight={200}
            fetching={loading}
            rowColor={({ updatedAt }) => {
              if (getDayDiff(updatedAt) >= 14) return 'red';
              if (getDayDiff(updatedAt) >= 7) return 'orange';
            }}
            columns={[
              {
                accessor: 'package',
                title: 'Číslo balíka',
                render: ({ id, package: packageNumber }) => (
                  <Anchor inherit to={`/order/${id}`} component={Link}>
                    {packageNumber}
                  </Anchor>
                ),
              },
              {
                accessor: 'customer.name',
                title: 'Meno zákazníka',
                render: ({ customer, address }) => (
                  <Group>
                    <Avatar src={customer.avatar} name={customer.name} color="initials" size="sm" />
                    <Stack gap={0}>
                      <Text inherit>{customer.name}</Text>
                      <Text inherit c="dimmed" size="sm">
                        {address?.phone ?? '-'}
                      </Text>
                    </Stack>
                  </Group>
                ),
              },
              {
                accessor: 'total',
                title: 'Spolu €',
                render: ({ total }) => `${total} €`,
              },
              {
                accessor: 'live.name',
                title: 'LIVE',
                render: ({ live }) => live.name,
              },
              {
                accessor: 'updatedAt',
                title: 'Zabalená',
                render: ({ createdAt, updatedAt }) => (
                  <Stack gap={0}>
                    <Text inherit>{dayjs(updatedAt).format('DD.MM.YYYY HH:mm:ss')}</Text>
                    <Text inherit c="dimmed">
                      {dayjs(createdAt).format('DD.MM.YYYY HH:mm:ss')}
                    </Text>
                  </Stack>
                ),
              },
              {
                accessor: 'packedBy',
                title: 'Zabalil',
              },
              {
                accessor: 'actions',
                title: (
                  <Group justify="flex-end">
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="green"
                      onClick={() => refetchOrders()}
                      loading={loading}
                    >
                      <IconRefresh size={16} />
                    </ActionIcon>
                  </Group>
                ),
                textAlign: 'right',
                render: ({ id, package: packageNumber, customer, total, updatedAt }) => (
                  <Group ml="auto" wrap="nowrap">
                    <Button
                      size="xs"
                      leftSection={<IconCheck size={16} />}
                      color={
                        getDayDiff(updatedAt) >= 14
                          ? 'red'
                          : getDayDiff(updatedAt) >= 7
                            ? 'orange'
                            : 'teal'
                      }
                      loading={updateOrderMutation.loading}
                      onClick={(e) => {
                        e.stopPropagation();

                        modals.openConfirmModal({
                          title: 'Potvrdiť prevzatie',
                          children: `Potvrdiť prevzatie objednávky č. ${packageNumber} pre zákazníka ${customer.name} zo dňa ${dayjs(updatedAt).format('DD.MM.YYYY')} v hodnote ${total} €?`,
                          labels: { confirm: 'Potvrdiť', cancel: 'Nie' },
                          onConfirm: async () => {
                            await updateOrder({
                              variables: {
                                where: { id },
                                data: { status: { set: OrderStatus.Delivered } },
                              },
                            });

                            setPackageNumber('');
                            setCustomerName('');

                            showNotification({
                              title: `Objednávka č. ${packageNumber} bola označená ako prevzatá`,
                              message: `Objednávka č. ${packageNumber} pre zákazníka ${customer.name} zo dňa ${dayjs(updatedAt).format('DD.MM.YYYY')} v hodnote ${total} € bola označená ako prevzatá`,
                            });

                            await sendMessage({
                              variables: {
                                orderId: id,
                                message: `Vaša objednávka č. ${packageNumber} zo dňa ${dayjs(updatedAt).format('DD.MM.YYYY')} v hodnote ${total} € bola označená ako prevzatá. Ďakujeme za vyzdvihnutie!`,
                              },
                            });
                          },
                        });
                      }}
                    >
                      Potvrdiť prevzatie ({getDayDiff(updatedAt)} dni)
                    </Button>
                    <ActionIcon
                      loading={sendMessageMutation.loading}
                      onClick={() => {
                        const defaultMessage = `Pripomíname, že Vaša objednávka č. ${packageNumber} zo dňa ${dayjs(updatedAt).format('DD.MM.YYYY')} v hodnote ${total} € stále čaká na predajni Sama Chalupku 2, dvere č. 3. Príďte si ju prosím vyzdvihnúť, inak ju budeme musieť zrušiť.`;
                        const messageRef = { current: defaultMessage };

                        modals.openConfirmModal({
                          title: `Odoslať správu zákazníkovi ${customer.name}`,
                          children: (
                            <Stack gap="xs">
                              <Text fw={500}>
                                Chcete odoslať správu zákazníkovi {customer.name}, ktorý si ešte
                                nevyzdvihol svoju objednávku č. {packageNumber} zo dňa{' '}
                                {dayjs(updatedAt).format('DD.MM.YYYY')} v hodnote {total} €?
                              </Text>
                              <Divider />
                              <Text fz="xs" c="dimmed">
                                Správa, ktorá bude odoslaná (môžete upraviť):
                              </Text>
                              <MessageEditor initial={defaultMessage} valueRef={messageRef} />
                            </Stack>
                          ),
                          labels: { confirm: 'Odoslať', cancel: 'Nie' },
                          onConfirm: () =>
                            sendMessage({
                              variables: {
                                orderId: id,
                                message: messageRef.current,
                              },
                            }),
                        });
                      }}
                    >
                      <IconSend size={16} />
                    </ActionIcon>
                  </Group>
                ),
              },
            ]}
            rowExpansion={{ content: ({ record }) => <PersonalOrderTable id={record.id} /> }}
          />
        </Tabs.Panel>
        <Tabs.Panel value="delivered">
          <DataTable
            withTableBorder
            striped
            highlightOnHover
            records={deliveredOrders}
            borderRadius="sm"
            minHeight={200}
            fetching={deliveredLoading}
            columns={[
              { accessor: 'id', title: '#', render: (_, index) => index + 1 },
              {
                accessor: 'package',
                title: 'Číslo balíka',
                render: ({ id, package: packageNumber }) => (
                  <Anchor inherit to={`/order/${id}`} component={Link}>
                    {packageNumber}
                  </Anchor>
                ),
              },
              {
                accessor: 'customer.name',
                title: 'Meno zákazníka',
                render: ({ customer, address }) => (
                  <Group>
                    <Avatar src={customer.avatar} name={customer.name} color="initials" size="sm" />
                    <Stack gap={0}>
                      <Text inherit>{customer.name}</Text>
                      <Text inherit c="dimmed" size="sm">
                        {address?.phone ?? '-'}
                      </Text>
                    </Stack>
                  </Group>
                ),
              },
              {
                accessor: 'total',
                title: 'Spolu €',
                render: ({ total }) => `${total} €`,
              },
              {
                accessor: 'live.name',
                title: 'LIVE',
                render: ({ live }) => live.name,
              },
              {
                accessor: 'createdAt',
                title: 'Prevzatá',
                render: ({ createdAt, updatedAt }) => (
                  <Stack gap={0}>
                    <Text inherit>{dayjs(updatedAt).format('DD.MM.YYYY HH:mm:ss')}</Text>
                    <Text inherit c="dimmed" size="sm">
                      {dayjs(createdAt).format('DD.MM.YYYY HH:mm:ss')}
                    </Text>
                  </Stack>
                ),
              },
              {
                accessor: 'packedBy',
                title: (
                  <Group justify="space-between">
                    <Text inherit>Zabalil</Text>
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="green"
                      onClick={() => refetchDelivered()}
                      loading={deliveredLoading}
                    >
                      <IconRefresh size={16} />
                    </ActionIcon>
                  </Group>
                ),
              },
            ]}
            rowExpansion={{ content: ({ record }) => <PersonalOrderTable id={record.id} /> }}
          />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
};
